import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';
import { formatCurrencyShort } from '../utils/currency';

const DEFAULT_STAGES = [
  { id: 'qualification', name: 'Qualification', color: '#3B82F6' },
  { id: 'discovery', name: 'Discovery', color: '#6366F1' },
  { id: 'proposal', name: 'Proposal', color: '#F59E0B' },
  { id: 'negotiation', name: 'Negotiation', color: '#0D9488' },
  { id: 'closed_won', name: 'Closed Won', color: '#10B981' },
  { id: 'closed_lost', name: 'Closed Lost', color: '#EF4444' }
];

function stageKey(stageName) {
  return stageName.toLowerCase().replace(/\s+/g, '_');
}

export default function PipelineBoard() {
  const [opportunities, setOpportunities] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [loading, setLoading] = useState(true);
  const [dragItem, setDragItem] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [oppRes, stagesRes] = await Promise.allSettled([
        apiFetch('/api/crm/opportunities'),
        apiFetch('/api/crm/pipeline-stages')
      ]);

      if (oppRes.status === 'fulfilled' && oppRes.value.ok) {
        const data = await oppRes.value.json();
        setOpportunities(data.data || data.opportunities || data || []);
      }

      if (stagesRes.status === 'fulfilled' && stagesRes.value.ok) {
        const data = await stagesRes.value.json();
        const stageList = data.data || data.stages || data;
        if (Array.isArray(stageList) && stageList.length > 0) {
          setStages(stageList.map(s => ({
            id: stageKey(s.name || s.stage_name),
            name: s.name || s.stage_name,
            color: s.color || '#3B82F6'
          })));
        }
      }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function getOppsByStage(stageName) {
    return opportunities.filter(o => stageKey(o.stage || '') === stageKey(stageName));
  }

  function stageTotal(stageName) {
    return getOppsByStage(stageName).reduce((sum, o) => sum + (parseFloat(o.estimated_value) || 0), 0);
  }

  function handleDragStart(e, opp) {
    setDragItem(opp);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', opp.id);
    setTimeout(() => {
      const el = document.getElementById(`pipeline-card-${opp.id}`);
      if (el) el.classList.add('dragging');
    }, 0);
  }

  function handleDragEnd(e) {
    const el = document.getElementById(`pipeline-card-${dragItem?.id}`);
    if (el) el.classList.remove('dragging');
    setDragItem(null);
    setDragOverStage(null);
  }

  function handleDragOver(e, stageName) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey(stageName));
  }

  function handleDragLeave(e, stageName) {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStage(null);
    }
  }

  async function handleDrop(e, stageName) {
    e.preventDefault();
    setDragOverStage(null);

    if (!dragItem) return;
    if (stageKey(dragItem.stage) === stageKey(stageName)) return;

    /* Optimistic update */
    setOpportunities(prev =>
      prev.map(o => o.id === dragItem.id ? { ...o, stage: stageName } : o)
    );

    try {
      await apiFetch(`/api/crm/opportunities/${dragItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({ stage: stageName })
      });
    } catch (err) {
      /* Revert on failure */
      fetchData();
    }
  }

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
        <span>Loading pipeline...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, marginTop: -16, fontSize: 13, color: '#64748B' }}>
        Drag and drop opportunities between stages to update their status.
      </div>

      <div className="pipeline-board">
        {stages.map(stage => {
          const stageOpps = getOppsByStage(stage.name);
          const total = stageTotal(stage.name);
          const isOver = dragOverStage === stage.id;

          return (
            <div className="pipeline-column" key={stage.id}>
              <div className="pipeline-column-header" style={{ borderTopColor: stage.color, borderTop: `3px solid ${stage.color}` }}>
                <div className="stage-name">{stage.name}</div>
                <div className="stage-meta">
                  <span>{stageOpps.length} deal{stageOpps.length !== 1 ? 's' : ''}</span>
                  <span className="total-value">{formatCurrencyShort(total)}</span>
                </div>
              </div>

              <div
                className={`pipeline-column-body${isOver ? ' drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, stage.name)}
                onDragLeave={(e) => handleDragLeave(e, stage.name)}
                onDrop={(e) => handleDrop(e, stage.name)}
              >
                {stageOpps.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px 10px', color: '#94A3B8', fontSize: 12 }}>
                    No opportunities
                  </div>
                )}

                {stageOpps.map(opp => (
                  <div
                    key={opp.id}
                    id={`pipeline-card-${opp.id}`}
                    className="pipeline-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, opp)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="opp-name">{opp.name}</div>
                    <div className="opp-customer">
                      {opp.customer_name || opp.company_name || 'No customer'}
                    </div>
                    <div className="opp-footer">
                      <span className="opp-value">{formatCurrencyShort(opp.estimated_value)}</span>
                      <span className="opp-probability">{opp.probability || 0}% prob.</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
